import { useQuery } from "@tanstack/react-query";

import { SiteDashboardsResponse, SiteIntegrationsResponse } from "~/api/Api.ts";
import { apiClient } from "~/api/ApiClient";
import { Authorizations } from "~/api/types";
import { QUERY_KEYS } from "~/pages/GoogleDashboardPage/constants";

type UseGoogleDashboardArgs = {
  siteId: number;
  enabled?: boolean;
};

export type SiteIntegrationsResponseWithAuth = SiteIntegrationsResponse & {
  authorizations?: Authorizations;
};

export type SiteDashboardResponseWithAuth = SiteDashboardsResponse & {
  authorizations?: Authorizations;
};

export const fetchSiteIntegrationsWithAuth = async (siteId: number) => {
  const response = await apiClient.sites.integrationsDetail(
    siteId,
    { "page[number]": 1, "page[size]": 100 },
    {
      headers: {
        "Authorize-Collection-Actions": "create",
        "Authorize-Member-Actions": "configuration,destroy,update",
      },
    },
  );

  const data = response.data as SiteIntegrationsResponseWithAuth;

  return {
    integrations: data.site_integrations,
    authorizations: data.authorizations,
  };
};

export const fetchSiteDashboardsWithAuth = async (siteId: number) => {
  const response = await apiClient.sites.dashboardsDetail(
    siteId,
    {},
    {
      headers: {
        "Authorize-Collection-Actions": "create",
        "Authorize-Member-Actions": "configuration,destroy,update",
      },
    },
  );

  const data = response.data as SiteDashboardResponseWithAuth;

  return {
    dashboards: data.site_dashboards,
    authorizations: data.authorizations,
  };
};

export const useGoogleDashboard = ({
  siteId,
  enabled = true,
}: UseGoogleDashboardArgs) => {
  const integrationsQuery = useQuery({
    queryKey: QUERY_KEYS.SITE_INTEGRATIONS(siteId),
    queryFn: () => fetchSiteIntegrationsWithAuth(siteId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled,
  });

  const dashboardsQuery = useQuery({
    queryKey: QUERY_KEYS.DASHBOARDS(siteId),
    queryFn: () => fetchSiteDashboardsWithAuth(siteId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    enabled,
  });

  const isLoading = enabled
    ? integrationsQuery.isLoading || dashboardsQuery.isLoading
    : false;

  const dashboards = dashboardsQuery.data?.dashboards ?? [];
  const integrations = integrationsQuery.data?.integrations ?? [];

  const canCreateDashboard = Boolean(
    dashboardsQuery.data?.authorizations?.collection?.includes("create"),
  );
  const canCreateIntegration = Boolean(
    integrationsQuery.data?.authorizations?.collection?.includes("create"),
  );

  // team has dashboards OR can create dashboard OR can create integration
  const shouldShowGoogleDash =
    dashboards.length > 0 || canCreateDashboard || canCreateIntegration;

  return {
    // data
    dashboards,
    integrations,

    // permissions
    canCreateDashboard,
    canCreateIntegration,

    // computed
    shouldShowGoogleDash,

    // query state
    isLoading,
    isFetching: integrationsQuery.isFetching || dashboardsQuery.isFetching,
    error: integrationsQuery.error ?? dashboardsQuery.error,
  };
};
